import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { cn } from "@/cn";
import { listProviders, updateProvider, type ProviderConfig } from "@/api/providers";
import { useClientPagination } from "@/hooks/usePagination";
import { PaginationBar } from "@/components/pagination-bar";
import {
  TYPE_LABELS,
  TYPE_COLORS,
  TYPE_FILTER_TABS,
  STATUS_FILTER_TABS,
  filterProviders,
  countEnabledProviders,
  type ProviderTypeFilter,
  type ProviderStatusFilter,
} from "@/lib/provider-ui";

export function SettingsProvidersPage() {
  const qc = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [filterTab, setFilterTab] = useState<ProviderTypeFilter>("all");
  const [statusFilter, setStatusFilter] = useState<ProviderStatusFilter>("all");

  const { data: providers = [], isLoading, error } = useQuery<ProviderConfig[]>({
    queryKey: ["providers"],
    queryFn: listProviders,
    staleTime: 30_000,
  });

  const filteredProviders = filterProviders(providers, {
    search: searchQuery,
    type: filterTab,
    status: statusFilter,
  });

  const enabledCount = countEnabledProviders(providers);

  const {
    pageItems,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    showingFrom,
    showingTo,
  } = useClientPagination(filteredProviders, [filterTab, searchQuery, statusFilter]);

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => updateProvider(id, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["providers"] }),
  });

  return (
    <div className="space-y-6">
      <header className="mb-8">
        <h1 className="text-2xl font-bold text-foreground">Signal providers</h1>
        <p className="text-muted-foreground">
          Shared catalog of hiring sources and market-signal providers. Enable sources
          globally, then select them for your account under{" "}
          <Link to="/lists?tab=providers" className="text-brand hover:underline">
            Lists → Providers
          </Link>
          . Credentials for NewsAPI / Apollo live under Integrations.
        </p>
      </header>

      {!isLoading && !error && providers.length > 0 && (
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <svg
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, description, or ID…"
                className="w-full rounded-md border border-border bg-card py-2 pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>
          </div>
          <span className="shrink-0 text-xs text-muted-foreground">
            {filteredProviders.length === 0
              ? `0 of ${providers.length} (${enabledCount} enabled)`
              : `Showing ${showingFrom}–${showingTo} of ${filteredProviders.length} (${enabledCount} enabled)`}
          </span>
        </div>
      )}

      {!isLoading && !error && providers.length > 0 && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-1">
            {TYPE_FILTER_TABS.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setFilterTab(tab)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-xs font-medium transition",
                  filterTab === tab
                    ? "bg-brand text-brand-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {tab === "all" ? "All types" : TYPE_LABELS[tab]}
              </button>
            ))}
          </div>
          <div className="flex gap-1">
            {STATUS_FILTER_TABS.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setStatusFilter(tab)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-xs font-medium transition",
                  statusFilter === tab
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {tab === "all" ? "All status" : tab === "enabled" ? "Enabled" : "Disabled"}
              </button>
            ))}
          </div>
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
        </div>
      )}
      {error && <p className="text-sm text-destructive">Failed to load providers.</p>}

      {!isLoading && !error && providers.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-8 text-center">
          <p className="text-sm text-muted-foreground">No providers configured.</p>
          <p className="mt-2 text-xs text-muted-foreground">
            Run <code className="rounded bg-muted px-1">npx tsx scripts/seed-providers.ts</code> in the backend package to seed the catalog.
          </p>
        </div>
      )}

      {!isLoading && !error && providers.length > 0 && filteredProviders.length === 0 && (
        <div className="rounded-lg border border-border bg-card p-8 text-center">
          <p className="text-sm text-muted-foreground">No providers match your filters.</p>
        </div>
      )}

      {!isLoading && !error && filteredProviders.length > 0 && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {pageItems.map((provider) => (
              <div
                key={provider.providerId}
                className={cn(
                  "flex flex-col rounded-lg border bg-card p-4 transition",
                  provider.enabled ? "border-border" : "border-border/50 bg-card/50 opacity-60",
                )}
              >
                <div className="mb-2 flex items-start justify-between gap-2">
                  <Link
                    to={`/settings/signals/${provider.providerId}`}
                    className="min-w-0 flex-1"
                  >
                    <p className="text-sm font-medium text-foreground hover:text-brand">
                      {provider.name}
                    </p>
                  </Link>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <span
                      className={cn(
                        "rounded-full px-2 py-0.5 text-[10px] font-medium",
                        TYPE_COLORS[provider.type],
                      )}
                    >
                      {TYPE_LABELS[provider.type]}
                    </span>
                    {provider.implemented === false && (
                      <span className="text-[10px] text-muted-foreground">
                        not implemented
                      </span>
                    )}
                  </div>
                </div>
                <Link
                  to={`/settings/signals/${provider.providerId}`}
                  className="mb-4 min-h-[2.5rem] flex-1"
                >
                  <p className="line-clamp-2 text-xs text-muted-foreground">
                    {provider.description || provider.providerId}
                  </p>
                </Link>
                <div className="flex items-center justify-between border-t border-border pt-3">
                  <label
                    className="relative inline-flex cursor-pointer items-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={provider.enabled}
                      onChange={() =>
                        toggleMutation.mutate({
                          id: provider.providerId,
                          enabled: !provider.enabled,
                        })
                      }
                      className="peer sr-only"
                    />
                    <div className="h-5 w-9 rounded-full border border-border bg-muted after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-foreground after:transition-all peer-checked:bg-brand peer-checked:after:translate-x-full peer-checked:after:bg-brand-foreground" />
                    <span className="ml-2 text-xs text-muted-foreground">
                      {provider.enabled ? "Enabled" : "Disabled"}
                    </span>
                  </label>
                  <Link
                    to={`/settings/signals/${provider.providerId}`}
                    className="rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground transition hover:text-foreground"
                  >
                    Details
                  </Link>
                </div>
              </div>
            ))}
          </div>
          <PaginationBar
            page={page}
            pageSize={pageSize}
            totalItems={filteredProviders.length}
            totalPages={totalPages}
            showingFrom={showingFrom}
            showingTo={showingTo}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </>
      )}
    </div>
  );
}
